import React, { useCallback, useEffect, useState, lazy } from 'react';
import { Minus, Plus, ShoppingBag } from 'lucide-react';
import { MenuItem, MenuItemOption, CartItemOption, ModifierOption, ResolvedModifierGroup } from '../../lib/types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { formatCurrency, cn } from '../../lib/utils';
import { useLanguage } from '../../lib/i18n';
import { useMenuItemOptions, useMenuItemModifiers } from '../../lib/hooks';
/** Color map for Heladería Congelados ice cream flavors */
const ICE_CREAM_FLAVOR_COLORS: Record<string, string> = {
  'Naranja Holandesa': '#FF8C00',
  Vainilla: '#F5E6CA',
  Chocolate: '#5C3317',
  Pecaditos: '#C4956A',
  Unicornio: '#FF69B4',
  'Ron frutas': '#B8860B',
  'Ron pasas': '#8B6914',
  Café: '#6F4E37',
  Oreo: '#2C2C2C',
  Snickers: '#A0522D',
  'Fresa Mora': '#DC143C',
  'Choco chips': '#7B3F00',
  Churchill: '#B0E0E6',
  Pistacho: '#93C572',
  'Veteado de mango': '#FFD700',
  'Piña colada': '#FFF3B0',
  Mango: '#FF9933',
  'Piña colada (base agua)': '#FFF3B0',
  'Mango (base agua)': '#FF9933'
};
/** Check if a group name is an ice cream flavor group */
function isIceCreamFlavorGroup(groupName: string): boolean {
  return groupName.startsWith('🍧') || groupName.startsWith('🍨') || groupName.startsWith('🍦');
}
/** Strip embedded price from option names like "Caja — ₡500" → "Caja" */
function stripEmbeddedPrice(name: string): string {
  return name.replace(/\s*[—–-]\s*₡[\d.,\s]+$/, '').trim();
}
/** Safely resolve max_select: treat 0, null, undefined as 1 (radio/single-select) */
function safeMaxSelect(val: number | null | undefined): number {
  return val && val > 0 ? val : 1;
}
/** Get the free_qty for an option group (all options in a group share the same free_qty) */
function getGroupFreeQty(groupOptions: MenuItemOption[]): number {
  const val = groupOptions[0]?.free_qty;
  return val && val > 0 ? val : 0;
}
/** Calculate the effective price delta for an option based on how many free selections remain */
function getEffectivePriceDelta(option: MenuItemOption, freeQty: number, currentFreeUsed: number): number {
  if (freeQty > 0 && currentFreeUsed < freeQty) {
    return 0; // This selection is free
  }
  return Number(option.price_delta) || 0;
}
interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  onAdd: (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => void;
  primaryColor?: string;
  theme?: 'default' | 'capricho';
}
export function ProductDetailModal({
  isOpen,
  onClose,
  item,
  onAdd,
  primaryColor,
  theme = 'default'
}: ProductDetailModalProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, MenuItemOption[]>>({});
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, ModifierOption[]>>({});
  const {
    options,
    loading
  } = useMenuItemOptions(item?.id);
  const {
    modifierGroups,
    loading: modifiersLoading
  } = useMenuItemModifiers(item?.id, item ? {
    name: item.name,
    restaurant_id: item.restaurant_id
  } : undefined);
  // Reset state ONLY when modal opens for a different item
  useEffect(() => {
    if (isOpen && item) {
      setQuantity(1);
      setNotes('');
      setSelectedOptions({});
      setSelectedModifiers({});
    }
  }, [isOpen, item?.id]);
  if (!item) return null;
  // Group options by option_group
  const groupedOptions = options.reduce((acc, option) => {
    const group = option.option_group;
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(option);
    return acc;
  }, {} as Record<string, MenuItemOption[]>);
  /** Check if a dependent option group should be visible based on its parent group selection */
  function isGroupVisible(groupName: string, groupOptions: MenuItemOption[]): boolean {
    const dependsOn = groupOptions[0]?.depends_on_group;
    if (!dependsOn) return true; // No dependency, always visible
    const parentSelections = selectedOptions[dependsOn] || [];
    if (parentSelections.length === 0) return false; // Parent has no selection
    // Hide if parent selection starts with "No" (e.g., "No salsas")
    return !parentSelections.some(sel => sel.name.toLowerCase().startsWith('no '));
  }
  // Sort option groups: ice cream flavors first, then everything else (toppings)
  const sortedGroupEntries = Object.entries(groupedOptions).sort(([a], [b]) => {
    const priority = (name: string) => {
      if (name.startsWith('🍦')) return 0; // Sabor de helado
      if (name.startsWith('🍧')) return 1; // Sabores de helado
      if (name.startsWith('🍨')) return 2; // Base agua
      return 10; // Toppings and everything else last
    };
    return priority(a) - priority(b);
  });
  const handleOptionSelect = (groupName: string, option: MenuItemOption) => {
    const maxSel = safeMaxSelect(option.max_select);
    setSelectedOptions(prev => {
      const group = prev[groupName] || [];
      const isAlreadySelected = group.some(o => o.id === option.id);
      // Deselect if already selected
      if (isAlreadySelected) {
        return {
          ...prev,
          [groupName]: group.filter(o => o.id !== option.id)
        };
      }
      // Radio behavior (single select): replace current selection
      if (maxSel === 1) {
        const newState = {
          ...prev,
          [groupName]: [option]
        };
        // If selecting a "No ..." option, clear all dependent groups
        if (option.name.toLowerCase().startsWith('no ')) {
          Object.entries(groupedOptions).forEach(([depGroupName, depGroupOpts]) => {
            if (depGroupOpts[0]?.depends_on_group === groupName) {
              newState[depGroupName] = [];
            }
          });
        }
        return newState;
      }
      // Multi-select: add if under limit
      if (group.length >= maxSel) {
        return prev; // at capacity
      }
      return {
        ...prev,
        [groupName]: [...group, option]
      };
    });
  };
  const handleModifierSelect = (groupId: string, option: ModifierOption, maxSelect: number) => {
    const maxSel = safeMaxSelect(maxSelect);
    setSelectedModifiers(prev => {
      const group = prev[groupId] || [];
      const isAlreadySelected = group.some(o => o.id === option.id);
      if (isAlreadySelected) {
        return {
          ...prev,
          [groupId]: group.filter(o => o.id !== option.id)
        };
      }
      if (maxSel === 1) {
        return {
          ...prev,
          [groupId]: [option]
        };
      }
      if (group.length >= maxSel) {
        return prev;
      }
      return {
        ...prev,
        [groupId]: [...group, option]
      };
    });
  };
  const handleAdd = () => {
    const cartOptions: CartItemOption[] = [];
    // For option groups with free_qty, calculate effective prices
    // Only include options from visible groups
    Object.entries(selectedOptions).forEach(([groupName, group]) => {
      const groupOpts = groupedOptions[groupName] || [];
      if (!isGroupVisible(groupName, groupOpts)) return; // Skip hidden groups
      const freeQty = getGroupFreeQty(groupOpts);
      group.forEach((opt, index) => {
        const effectivePrice = freeQty > 0 && index < freeQty ? 0 : Number(opt.price_delta) || 0;
        cartOptions.push({
          option_name: opt.name,
          option_group: groupName,
          price_delta: effectivePrice
        });
      });
    });
    Object.values(selectedModifiers).forEach(group => {
      group.forEach(opt => {
        cartOptions.push({
          option_name: opt.name,
          price_delta: Number(opt.price_delta) || 0
        });
      });
    });
    onAdd(item, quantity, cartOptions, notes);
    onClose();
  };
  // Validation: required groups must have at least one selection (skip hidden dependent groups)
  const optionsValid = sortedGroupEntries.every(([groupName, groupOptions]) => {
    if (!isGroupVisible(groupName, groupOptions)) return true; // Hidden groups are always valid
    const firstOption = groupOptions[0];
    if (!firstOption.is_required) return true;
    return (selectedOptions[groupName]?.length ?? 0) > 0;
  });
  const modifiersValid = modifierGroups.every(mg => {
    if (!mg.group.is_required) return true;
    return (selectedModifiers[mg.group.id]?.length ?? 0) >= (mg.group.min_select || 1);
  });
  const isValid = optionsValid && modifiersValid;
  const optionsTotal = Object.entries(selectedOptions).reduce((sum, [groupName, group]) => {
    const groupOpts = groupedOptions[groupName] || [];
    if (!isGroupVisible(groupName, groupOpts)) return sum; // Skip hidden groups
    const freeQty = getGroupFreeQty(groupOpts);
    return sum + group.reduce((gSum, opt, index) => {
      const effectivePrice = freeQty > 0 && index < freeQty ? 0 : Number(opt.price_delta) || 0;
      return gSum + effectivePrice;
    }, 0);
  }, 0);
  const modifiersTotal = Object.values(selectedModifiers).reduce((sum, group) => sum + group.reduce((gSum, opt) => gSum + (Number(opt.price_delta) || 0), 0), 0);
  const totalPrice = ((Number(item.price) || 0) + optionsTotal + modifiersTotal) * quantity;
  const itemName = item.name;
  const itemDesc = item.description;
  return <Modal isOpen={isOpen} onClose={onClose} title={itemName} data-id="element-1529">
      <div className="flex flex-col h-[calc(100dvh-2rem)] sm:h-auto sm:max-h-[85vh]" data-id="element-1530">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-1531">
          {/* Header Image */}
          {item.image_url && Number(item.restaurant_id) !== 24 && <div className="relative w-full h-32 sm:h-64 bg-[#141414]" data-id="element-1532">
              <img src={item.image_url} alt={itemName} className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-1533" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] to-transparent" data-id="element-1534" />
            </div>}

          <div className="p-3 sm:p-6 space-y-3 sm:space-y-6" data-id="element-1535">
            {/* Title & Description */}
            <div data-id="element-1536">
              <div className="flex justify-between items-start gap-4 mb-2" data-id="element-1537">
                <h2 className="text-xl sm:text-2xl font-black text-white leading-tight" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-1538">
                  {itemName}
                </h2>
                <span className="text-lg sm:text-xl font-bold text-[#C9A34E] whitespace-nowrap" data-id="element-1539">
                  {formatCurrency(Number(item.price) || 0)}
                </span>
              </div>
              {itemDesc && <p className="text-white/60 leading-relaxed text-sm" data-id="element-1540">
                  {itemDesc}
                </p>}
            </div>

            {/* ═══ OPTION GROUPS (menu_item_options) ═══ */}
            {sortedGroupEntries.filter(([groupName, groupOptions]) => isGroupVisible(groupName, groupOptions)).map(([groupName, groupOptions]) => {
            const firstOption = groupOptions[0];
            const isRequired = firstOption.is_required;
            const maxSel = safeMaxSelect(firstOption.max_select);
            const freeQty = getGroupFreeQty(groupOptions);
            const currentSelections = selectedOptions[groupName] || [];
            const selectedCount = currentSelections.length;
            const localizedGroupName = t(`group_${groupName}`) !== `group_${groupName}` ? t(`group_${groupName}`) : groupName;
            return <div key={groupName} className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5" data-id="element-1541">
                    <div className="flex items-baseline justify-between mb-2 sm:mb-4" data-id="element-1542">
                      <div data-id="element-1543">
                        <h3 className="text-base sm:text-lg font-bold text-white mb-1" data-id="element-1544">
                          {localizedGroupName}
                        </h3>
                        <p className="text-sm text-white/40" data-id="element-1545">
                          {isRequired ? t('required') : freeQty > 0 ? `${freeQty} incluidas · Extra +${formatCurrency(Number(firstOption.price_delta) || 0)}` : maxSel >= 20 ? 'Selecciona los que quieras' : `${t('select_up_to')} ${maxSel}`}
                        </p>
                        {freeQty > 0 && <p className="text-xs text-emerald-400/80 mt-0.5" data-id="element-1546">
                            {selectedCount <= freeQty ? `${freeQty - selectedCount} gratis restante${freeQty - selectedCount !== 1 ? 's' : ''}` : `${selectedCount - freeQty} extra${selectedCount - freeQty !== 1 ? 's' : ''} (+${formatCurrency((selectedCount - freeQty) * (Number(firstOption.price_delta) || 0))})`}
                          </p>}
                      </div>
                      {isRequired && <span className="bg-[#C9A34E]/10 text-[#C9A34E] text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-1547">
                          {t('required')}
                        </span>}
                    </div>

                    <div className="space-y-1.5 sm:space-y-2" data-id="element-1548">
                      {groupOptions.map(option => {
                  const isSelected = currentSelections.some(o => o.id === option.id);
                  // Only disable in multi-select when at capacity
                  const isDisabled = maxSel > 1 && !isSelected && selectedCount >= maxSel;
                  const flavorColor = isIceCreamFlavorGroup(groupName) && !option.name.toLowerCase().includes('cookie monster') ? ICE_CREAM_FLAVOR_COLORS[option.name] : undefined;
                  // Determine the display price for this option
                  const selectionIndex = isSelected ? currentSelections.findIndex(o => o.id === option.id) : selectedCount;
                  const wouldBeFree = freeQty > 0 && selectionIndex < freeQty;
                  return <button key={option.id} type="button" disabled={isDisabled} onClick={() => handleOptionSelect(groupName, option)} className={cn('w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all text-left', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]/5' : 'border-white/5 hover:border-white/20 bg-white/5', isDisabled && 'opacity-50 cursor-not-allowed')} data-id="element-1549">
                            <div className="flex items-center gap-3" data-id="element-1550">
                              <div className={cn('w-4 h-4 sm:w-5 sm:h-5 border-2 flex items-center justify-center transition-colors shrink-0', maxSel > 1 || freeQty > 0 ? 'rounded-md' : 'rounded-full', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]' : 'border-white/20')} data-id="element-1551">
                                {isSelected && (maxSel > 1 || freeQty > 0 ? <svg className="w-3 h-3 text-[#0B0B0B]" viewBox="0 0 12 12" fill="none" data-id="element-1552">
                                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1553" />
                                    </svg> : <div className="w-2 h-2 bg-[#0B0B0B] rounded-full" data-id="element-1554" />)}
                              </div>
                              {flavorColor && <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full shrink-0 ring-1 ring-white/10" style={{
                        backgroundColor: flavorColor
                      }} data-id="element-1555" />}
                              <span className={cn('font-medium', isSelected ? 'text-white' : 'text-white/80')} data-id="element-1556">
                                {stripEmbeddedPrice(option.name)}
                              </span>
                            </div>
                            {freeQty > 0 ? isSelected && wouldBeFree ? <span className="text-xs font-bold text-emerald-400" data-id="element-1557">
                                  Incluida
                                </span> : !isSelected && selectedCount < freeQty ? <span className="text-xs text-white/30" data-id="element-1558">
                                  Incluida
                                </span> : <span className="text-sm font-bold text-[#C9A34E]" data-id="element-1559">
                                  +
                                  {formatCurrency(Number(option.price_delta) || 0)}
                                </span> : Number(option.price_delta ?? 0) !== 0 ? <span className="text-sm font-bold text-[#C9A34E] shrink-0" data-id="element-1560">
                                {Number(option.price_delta) > 0 ? `+${formatCurrency(Number(option.price_delta))}` : `-${formatCurrency(Math.abs(Number(option.price_delta)))}`}
                              </span> : null}
                          </button>;
                })}
                    </div>
                  </div>;
          })}

            {/* ═══ MODIFIER GROUPS (modifier_groups + modifier_options) ═══ */}
            {modifierGroups.length > 0 && modifierGroups.map(mg => {
            const maxSel = safeMaxSelect(mg.group.max_select);
            const currentSelections = selectedModifiers[mg.group.id] || [];
            const selectedCount = currentSelections.length;
            return <div key={mg.group.id} className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5" data-id="element-1561">
                    <div className="flex items-baseline justify-between mb-2 sm:mb-4" data-id="element-1562">
                      <div data-id="element-1563">
                        <h3 className="text-base sm:text-lg font-bold text-white mb-1" data-id="element-1564">
                          {mg.group.name}
                        </h3>
                        {mg.group.description && <p className="text-sm text-white/40" data-id="element-1565">
                            {mg.group.description}
                          </p>}
                        <p className="text-xs text-white/30 mt-1" data-id="element-1566">
                          {mg.group.is_required ? `${t('required')} · ${t('select_up_to')} ${maxSel}` : `${t('select_up_to')} ${maxSel}`}
                        </p>
                      </div>
                      {mg.group.is_required && <span className="bg-[#C9A34E]/10 text-[#C9A34E] text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-1567">
                          {t('required')}
                        </span>}
                    </div>

                    <div className="space-y-1.5 sm:space-y-2" data-id="element-1568">
                      {mg.options.map(option => {
                  const isSelected = currentSelections.some(o => o.id === option.id);
                  const isDisabled = maxSel > 1 && !isSelected && selectedCount >= maxSel;
                  return <button key={option.id} type="button" disabled={isDisabled} onClick={() => handleModifierSelect(mg.group.id, option, mg.group.max_select)} className={cn('w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all text-left', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]/5' : 'border-white/5 hover:border-white/20 bg-white/5', isDisabled && 'opacity-50 cursor-not-allowed')} data-id="element-1569">
                            <div className="flex items-center gap-3" data-id="element-1570">
                              <div className={cn('w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-colors shrink-0', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]' : 'border-white/20')} data-id="element-1571">
                                {isSelected && <div className="w-2 h-2 bg-[#0B0B0B] rounded-full" data-id="element-1572" />}
                              </div>
                              <span className={cn('font-medium', isSelected ? 'text-white' : 'text-white/80')} data-id="element-1573">
                                {stripEmbeddedPrice(option.name)}
                              </span>
                            </div>
                            {(() => {
                      const delta = Number(option.price_delta ?? 0);
                      if (delta > 0) return <span className="text-sm font-bold text-[#C9A34E]" data-id="element-1574">
                                    +{formatCurrency(delta)}
                                  </span>;
                      if (delta < 0) return <span className="text-sm font-bold text-emerald-400" data-id="element-1575">
                                    -{formatCurrency(Math.abs(delta))}
                                  </span>;
                      return <span className="text-xs text-white/30" data-id="element-1576">
                                  {t('included') !== 'included' ? t('included') : 'Incluido'}
                                </span>;
                    })()}
                          </button>;
                })}
                    </div>
                  </div>;
          })}

            {/* Notes */}
            <div className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5" data-id="element-1577">
              <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3" data-id="element-1578">
                {t('checkout_notes')}
              </h3>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('notes_placeholder')} className="resize-none h-16 sm:h-24 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A34E] focus:ring-[#C9A34E]" data-id="element-1579" />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-3 sm:p-6 bg-[#0B0B0B] border-t border-white/10 mt-auto" data-id="element-1580">
          <div className="flex items-center gap-3 sm:gap-4" data-id="element-1581">
            {/* Quantity Selector */}
            <div className="flex items-center bg-[#141414] rounded-xl border border-white/10 p-1" data-id="element-1582">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-id="element-1583">
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1584" />
              </button>
              <span className="w-8 sm:w-12 text-center font-bold text-base sm:text-lg text-white" data-id="element-1585">
                {quantity}
              </span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-id="element-1586">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1587" />
              </button>
            </div>

            {/* Add to Cart Button */}
            <Button onClick={handleAdd} disabled={!isValid} className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-bold bg-[#C9A34E] hover:bg-[#B8923D] text-[#0B0B0B]" data-id="element-1588">
              <div className="flex items-center justify-between w-full px-2" data-id="element-1589">
                <span data-id="element-1590">{t('add_to_cart')}</span>
                <span data-id="element-1591">{formatCurrency(totalPrice)}</span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Modal>;
}